import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  Grid,
  Stack,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { ContainerCard, DateTextField } from "../../components";
import LoaderCard from "../../components/cards/LoaderCard";
import NoDataCard from "../../components/cards/NoDataCard";
import { getAnalyticsSummary } from "../../services/api";
import { useAuth, useToast } from "../../contexts";
import dayjs from "dayjs";
import "./index.css";

const PERIOD_CHIPS = [
  { key: "today", title: "Today" },
  { key: "7d", title: "7 days" },
  { key: "30d", title: "30 days" },
  { key: "90d", title: "90 days" },
  { key: "all", title: "All time" },
  { key: "custom", title: "Custom" },
];

const TREND_VIEWS = [
  { key: "registrations", title: "New users" },
  { key: "engagements", title: "Engagements" },
  { key: "revenue", title: "Revenue" },
];

const fmtNum = (value) => {
  const num = Number(value ?? 0);
  return (Number.isFinite(num) ? num : 0).toLocaleString("en-IN");
};

const fmtRupee = (value) => {
  const num = Number(value ?? 0);
  return (Number.isFinite(num) ? num : 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });
};

const fmtPct = (value) => {
  const num = Number(value ?? 0);
  return `${(Number.isFinite(num) ? num : 0).toFixed(1)}%`;
};

const fmtAxis = (value) => {
  if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${Math.round(value)}`;
};

const fmtDay = (date) => {
  if (!date) return "";
  const d = dayjs(date);
  return d.isValid() ? d.format("DD MMM") : "";
};

function KpiCard(props) {
  const { label, value, caption, alert } = props;
  return (
    <Box
      className={alert ? "analytics-kpi analytics-kpi--alert" : "analytics-kpi"}
    >
      <Typography className="analytics-kpi-value">{value}</Typography>
      <Typography className="analytics-kpi-label">{label}</Typography>
      {caption ? (
        <Typography className="analytics-kpi-caption">{caption}</Typography>
      ) : null}
    </Box>
  );
}

function SectionCard(props) {
  const { title, caption, children } = props;
  return (
    <ContainerCard>
      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{title}</Typography>
      {caption ? (
        <Typography sx={{ fontSize: 13, color: "#757575", mt: 0.5 }}>
          {caption}
        </Typography>
      ) : null}
      <Box className="analytics-kpi-grid" sx={{ mt: 2 }}>
        {children}
      </Box>
    </ContainerCard>
  );
}

function PlatformSplit(props) {
  const { android, ios } = props;
  const total = android + ios;
  const androidPct = total > 0 ? Math.round((android / total) * 100) : 0;
  const iosPct = total > 0 ? Math.round((ios / total) * 100) : 0;
  return (
    <Box className="analytics-kpi">
      <Typography className="analytics-kpi-value">
        {`A ${fmtNum(android)} · i ${fmtNum(ios)}`}
      </Typography>
      <Typography className="analytics-kpi-label">
        Android / iOS devices
      </Typography>
      <Box className="analytics-split">
        <Box
          className="analytics-split-bar analytics-split-bar--android"
          sx={{ width: `${androidPct}%` }}
        />
        <Box
          className="analytics-split-bar analytics-split-bar--ios"
          sx={{ width: `${iosPct}%` }}
        />
      </Box>
      <Typography className="analytics-kpi-caption">
        {`Android ${androidPct}% · iOS ${iosPct}%`}
      </Typography>
    </Box>
  );
}

function TrendChart(props) {
  const { trend, view } = props;
  const width = 720;
  const height = 240;
  const padLeft = 52;
  const padRight = 16;
  const padTop = 16;
  const padBottom = 28;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;

  const buckets = Array.isArray(trend) ? trend : [];
  const count = buckets.length;

  if (count === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: "#757575", py: 6 }}>
        No trend data for this period
      </Typography>
    );
  }

  const mainKey = view;
  const overlayKey = view === "registrations" ? "engagements" : null;

  const mainValues = buckets.map((b) => {
    const num = Number(b?.[mainKey] ?? 0);
    return Number.isFinite(num) ? num : 0;
  });
  const overlayValues = overlayKey
    ? buckets.map((b) => {
        const num = Number(b?.[overlayKey] ?? 0);
        return Number.isFinite(num) ? num : 0;
      })
    : [];

  const maxValue = Math.max(...mainValues, ...overlayValues, 1);
  const xOf = (i) =>
    count === 1
      ? padLeft + innerWidth / 2
      : padLeft + (i / (count - 1)) * innerWidth;
  const yOf = (v) => padTop + (1 - v / maxValue) * innerHeight;

  const mainPoints = mainValues.map((v, i) => ({
    x: Math.round(xOf(i) * 10) / 10,
    y: Math.round(yOf(v) * 10) / 10,
  }));
  const linePath = mainPoints
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
    .join(" ");
  const areaPath = `${linePath} L${mainPoints[mainPoints.length - 1].x},${
    padTop + innerHeight
  } L${mainPoints[0].x},${padTop + innerHeight} Z`;

  const slot = innerWidth / count;
  const barWidth = Math.min(slot * 0.6, 12);
  const gridValues = [maxValue, maxValue / 2, 0];

  return (
    <Box>
      <svg
        className="analytics-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
      >
        {gridValues.map((gv, i) => {
          const y = Math.round(yOf(gv) * 10) / 10;
          return (
            <Fragment key={i}>
              <line
                x1={padLeft}
                x2={width - padRight}
                y1={y}
                y2={y}
                className="analytics-chart-grid"
                vectorEffect="non-scaling-stroke"
              />
              <text x={padLeft - 8} y={y + 4} className="analytics-chart-axis">
                {fmtAxis(gv)}
              </text>
            </Fragment>
          );
        })}
        {overlayValues.length > 0 &&
          overlayValues.map((v, i) => {
            const barHeight = Math.max(
              padTop + innerHeight - yOf(v),
              v > 0 ? 2 : 0
            );
            const x =
              count === 1
                ? padLeft + (innerWidth - barWidth) / 2
                : padLeft + i * slot + (slot - barWidth) / 2;
            return (
              <rect
                key={i}
                x={Math.round(x * 10) / 10}
                y={Math.round((padTop + innerHeight - barHeight) * 10) / 10}
                width={Math.round(barWidth * 10) / 10}
                height={Math.round(barHeight * 10) / 10}
                className="analytics-chart-bar"
              />
            );
          })}
        {mainPoints.length > 1 ? (
          <Fragment>
            <path d={areaPath} className="analytics-chart-area" />
            <path
              d={linePath}
              className="analytics-chart-line"
              vectorEffect="non-scaling-stroke"
            />
          </Fragment>
        ) : (
          <circle
            cx={mainPoints[0].x}
            cy={mainPoints[0].y}
            r={4}
            className="analytics-chart-dot"
          />
        )}
        {[0, Math.floor((count - 1) / 2), count - 1]
          .filter((i, idx, arr) => arr.indexOf(i) === idx)
          .map((i) => {
            const x = xOf(i);
            const anchor = i === 0 ? "start" : i === count - 1 ? "end" : "middle";
            return (
              <text
                key={i}
                x={x}
                y={height - 8}
                className="analytics-chart-axis"
                textAnchor={anchor}
              >
                {fmtDay(buckets[i]?.date)}
              </text>
            );
          })}
      </svg>
      {overlayKey ? (
        <Typography sx={{ fontSize: 12, color: "#757575", mt: 1 }}>
          Bars = engagement events (likes + bookmarks + feedback)
        </Typography>
      ) : null}
    </Box>
  );
}

export function Analytics(props) {
  const [period, setPeriod] = useState("7d");
  const [customFrom, setCustomFrom] = useState(dayjs().subtract(6, "day"));
  const [customTo, setCustomTo] = useState(dayjs());
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [trendView, setTrendView] = useState("registrations");

  const { showToast } = useToast();
  const { logout } = useAuth();
  const fetchEpoch = useRef(0);

  const getAnalytics = () => {
    if (period === "custom") {
      if (!customFrom || !customTo) {
        return;
      }
      if (customFrom.isAfter(customTo, "day")) {
        showToast("From date cannot be after To date", "error");
        return;
      }
      if (customTo.diff(customFrom, "day") > 365) {
        showToast("Custom range is limited to 366 days", "error");
        return;
      }
    }
    const epoch = ++fetchEpoch.current;
    setIsLoading(true);
    const params = { period: period };
    if (period === "custom") {
      params.from = customFrom.format("YYYY-MM-DD");
      params.to = customTo.format("YYYY-MM-DD");
    }
    getAnalyticsSummary(params)
      .then((res) => {
        if (epoch !== fetchEpoch.current) {
          return; // stale response — a newer filter change already superseded it
        }
        setSummary(res.data?.data ?? null);
        setIsLoading(false);
      })
      .catch((err) => {
        if (epoch !== fetchEpoch.current) {
          return;
        }
        setIsLoading(false);
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  useEffect(() => {
    getAnalytics();
  }, [period, customFrom, customTo]);

  const handlePeriodClick = (key) => {
    if (key === period) return;
    if (key === "custom") {
      setCustomFrom(dayjs().subtract(6, "day"));
      setCustomTo(dayjs());
    }
    setPeriod(key);
  };

  const handleCustomFromChange = (date) => {
    setCustomFrom(date ?? null);
  };

  const handleCustomToChange = (date) => {
    setCustomTo(date ?? null);
  };

  const handleTrendViewClick = (key) => {
    setTrendView(key);
  };

  const users = summary?.users;
  const subscriptions = summary?.subscriptions;
  const revenue = summary?.revenue;
  const engagement = summary?.engagement;
  const push = summary?.push;
  const content = summary?.content;
  const periodLabel = summary?.period?.label ?? "";
  const trendLabel = summary?.trend_label ?? periodLabel;
  const topArticles = Array.isArray(content?.top_articles)
    ? content.top_articles
    : [];
  const paymentsSkipped = Number(revenue?.payments_skipped ?? 0);

  return (
    <Fragment>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
              Analytics
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#757575" }}>
              {periodLabel ? `${periodLabel} (IST)` : ""}
            </Typography>
          </Stack>
        </Grid>
        <Grid item>
          <ContainerCard>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
              {PERIOD_CHIPS.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className={
                    period === chip.key
                      ? "analytics-chip analytics-chip--active"
                      : "analytics-chip"
                  }
                  onClick={() => handlePeriodClick(chip.key)}
                >
                  {chip.title}
                </button>
              ))}
            </Stack>
            {period === "custom" && (
              <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
                <Box sx={{ width: 220 }}>
                  <DateTextField
                    label="From Date"
                    value={customFrom ?? null}
                    onChange={handleCustomFromChange}
                  />
                </Box>
                <Box sx={{ width: 220 }}>
                  <DateTextField
                    label="To Date"
                    value={customTo ?? null}
                    onChange={handleCustomToChange}
                  />
                </Box>
              </Stack>
            )}
          </ContainerCard>
        </Grid>
        {isLoading && (
          <Grid item>
            <LoaderCard />
          </Grid>
        )}
        {!isLoading && (
          <Fragment>
            {!summary && (
              <Grid item>
                <NoDataCard title={"No Analytics Data Found"} />
              </Grid>
            )}
            {summary && (
              <Fragment>
                <Grid item xs>
                  <ContainerCard>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ flexWrap: "wrap", rowGap: 1, mb: 1 }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                          Trend over time
                        </Typography>
                        <Typography
                          sx={{ fontSize: 13, color: "#757575", mt: 0.5 }}
                        >
                          {`${trendLabel ? `${trendLabel} · ` : ""}Daily numbers (IST)`}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        {TREND_VIEWS.map((v) => (
                          <button
                            key={v.key}
                            type="button"
                            className={
                              trendView === v.key
                                ? "analytics-chip analytics-chip--active"
                                : "analytics-chip"
                            }
                            onClick={() => handleTrendViewClick(v.key)}
                          >
                            {v.title}
                          </button>
                        ))}
                      </Stack>
                    </Stack>
                    <TrendChart trend={summary?.trend} view={trendView} />
                  </ContainerCard>
                </Grid>
                <Grid item xs>
                  <SectionCard
                    title="Users"
                    caption="Who is using Sunnivoice"
                  >
                    <KpiCard
                      label="New users"
                      value={fmtNum(users?.new)}
                      caption="Signed up in this period"
                    />
                    <KpiCard
                      label="Logins"
                      value={fmtNum(users?.logins)}
                      caption="Unique numbers that requested an OTP"
                    />
                    <KpiCard
                      label="Push reached"
                      value={fmtNum(users?.push_reached)}
                      caption="Unique users who received a notification"
                    />
                    <KpiCard
                      label="Engaged"
                      value={fmtNum(users?.engaged)}
                      caption="Liked, bookmarked or read a push"
                    />
                    <KpiCard
                      label="Total users"
                      value={fmtNum(users?.total)}
                      caption="All active accounts"
                    />
                    <PlatformSplit
                      android={Number(users?.platform_android ?? 0)}
                      ios={Number(users?.platform_ios ?? 0)}
                    />
                  </SectionCard>
                </Grid>
                <Grid item xs>
                  <SectionCard
                    title="Subscriptions"
                    caption="New, active and soon-to-expire plans"
                  >
                    <KpiCard
                      label="New subscriptions"
                      value={fmtNum(subscriptions?.new)}
                      caption="Plans started in this period"
                    />
                    <KpiCard
                      label="Active subscriptions"
                      value={fmtNum(subscriptions?.active)}
                      caption="Running right now"
                    />
                    <KpiCard
                      label="Expiring in 7 days"
                      value={fmtNum(subscriptions?.expiring_7d)}
                      alert={Number(subscriptions?.expiring_7d ?? 0) > 0}
                    />
                    <KpiCard
                      label="Conversion"
                      value={fmtPct(subscriptions?.conversion_pct)}
                      caption="New subscriptions ÷ new users"
                    />
                  </SectionCard>
                </Grid>
                <Grid item xs>
                  <SectionCard
                    title="Revenue"
                    caption="Money received in this period"
                  >
                    <KpiCard
                      label="Payments received"
                      value={fmtRupee(revenue?.payments_total)}
                      caption="Verified successful payments"
                    />
                    <KpiCard
                      label="Payment count"
                      value={fmtNum(revenue?.payments_count)}
                      caption={
                        paymentsSkipped > 0
                          ? `${fmtNum(paymentsSkipped)} rows skipped (unreadable amount)`
                          : undefined
                      }
                    />
                    <KpiCard
                      label="Magazine purchases"
                      value={fmtNum(revenue?.magazine_purchases)}
                    />
                    <KpiCard
                      label="Magazine revenue"
                      value={fmtRupee(revenue?.magazine_revenue)}
                    />
                  </SectionCard>
                </Grid>
                <Grid item xs>
                  <SectionCard
                    title="Engagement"
                    caption="How readers interact with content"
                  >
                    <KpiCard label="Likes" value={fmtNum(engagement?.likes)} />
                    <KpiCard
                      label="Bookmarks"
                      value={fmtNum(engagement?.bookmarks)}
                    />
                    <KpiCard
                      label="Feedback"
                      value={fmtNum(engagement?.feedback)}
                    />
                    <KpiCard
                      label="Push delivered"
                      value={fmtNum(engagement?.push_delivered)}
                    />
                    <KpiCard
                      label="Push read"
                      value={fmtNum(engagement?.push_read)}
                    />
                    <KpiCard
                      label="Read rate"
                      value={fmtPct(engagement?.read_rate_pct)}
                      caption="Push read ÷ push delivered"
                    />
                  </SectionCard>
                </Grid>
                <Grid item xs>
                  <SectionCard
                    title="Push notifications"
                    caption="Delivery health of campaigns"
                  >
                    <KpiCard
                      label="Campaigns sent"
                      value={fmtNum(push?.campaigns)}
                    />
                    <KpiCard
                      label="Delivery rate"
                      value={fmtPct(push?.delivered_pct)}
                    />
                    <KpiCard
                      label="Invalid tokens"
                      value={fmtNum(push?.invalid_tokens)}
                      alert={Number(push?.invalid_tokens ?? 0) > 0}
                      caption="Devices that can no longer receive pushes"
                    />
                  </SectionCard>
                </Grid>
                <Grid item xs>
                  <SectionCard
                    title="Content"
                    caption="What was published and what readers loved"
                  >
                    <KpiCard
                      label="Articles published"
                      value={fmtNum(content?.articles_published)}
                      caption="Released and visible in this period"
                    />
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
                      >
                        Top 5 articles (likes + bookmarks)
                      </Typography>
                      {topArticles.length === 0 ? (
                        <Typography sx={{ fontSize: 14, color: "#757575" }}>
                          No articles in this period
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                                  Article
                                </TableCell>
                                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                                  Likes
                                </TableCell>
                                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                                  Bookmarks
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {topArticles.map((a, i) => (
                                <TableRow key={a?.id ?? i} hover>
                                  <TableCell>{a?.title ?? "-"}</TableCell>
                                  <TableCell>{fmtNum(a?.likes)}</TableCell>
                                  <TableCell>{fmtNum(a?.bookmarks)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  </SectionCard>
                </Grid>
              </Fragment>
            )}
          </Fragment>
        )}
      </Grid>
    </Fragment>
  );
}
